import { useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { Syringe } from "lucide-react";

import { medicalService, type ClinicLearner, type Immunization, type MedicalRecord } from "../../services/medicalService";
import { apiErrorMessage } from "../../utils/apiError";
import { todayIso } from "../../utils/dates";
import { Field, FormSection } from "../FormLayout";
import { FormModal } from "../FormModal";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { DetailsModal } from "../register/DetailsModal";
import { Notice, type NoticeState } from "../register/RecordParts";
import { ClinicalAlerts, LearnerPick } from "./ClinicParts";
import { REACTION_SEVERITIES, VACCINE_TYPES, day, immunisationConfig, labelFrom, nextDoseStatus, sortLearners } from "./medicalLogic";

const severityDot = (severity: string) => (severity === "severe" ? "bg-red-500" : severity === "moderate" ? "bg-amber-500" : severity === "mild" ? "bg-sky-500" : "bg-gray-300");

function ImmunisationForm({ learners, records, onClose, onDone }: {
  learners: ClinicLearner[];
  records: MedicalRecord[];
  onClose: () => void;
  onDone: (row: Immunization) => void;
}) {
  const today = todayIso();
  const [learner, setLearner] = useState("");
  const [vaccine, setVaccine] = useState("tetanus");
  const [name, setName] = useState(labelFrom(VACCINE_TYPES, "tetanus"));
  const [given, setGiven] = useState(today);
  const [severity, setSeverity] = useState("none");
  const record = records.find((row) => String(row.student) === learner);
  const save = useMutation({ mutationFn: medicalService.recordImmunization, onSuccess: onDone });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const text = (field: string) => String(data.get(field) ?? "").trim();
    save.mutate({
      student_id: Number(learner),
      vaccine_type: vaccine,
      vaccine_name: name.trim(),
      vaccination_date: given,
      batch_number: text("batch_number") || null,
      manufacturer: text("manufacturer") || null,
      expiry_date: text("expiry_date") || null,
      administration_site: text("administration_site") || null,
      administration_route: text("administration_route") || null,
      next_dose_due: text("next_dose_due") || null,
      next_dose_notes: text("next_dose_notes") || null,
      reaction_severity: severity,
      adverse_reactions: severity === "none" ? null : text("adverse_reactions") || null,
      notes: text("notes") || null,
    });
  };

  return (
    <FormModal
      id="immunisation"
      title="Record an immunisation"
      busy={save.isPending}
      error={save.isError ? apiErrorMessage(save.error, "The immunisation could not be recorded.") : undefined}
      saveLabel="Record it"
      note="Recorded as given by you."
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="The learner" divided={false}>
        <LearnerPick learners={learners} value={learner} onChange={setLearner} />
        {learner && (
          <div className="sm:col-span-2">
            <ClinicalAlerts record={record} missingText="This learner has no health record yet, so allergies are unknown." />
          </div>
        )}
      </FormSection>

      <FormSection title="The vaccine">
        <Field label="Vaccine">
          <select
            value={vaccine}
            onChange={(event) => {
              const next = event.target.value;
              // Keep a name someone typed; follow the choice otherwise.
              if (name === labelFrom(VACCINE_TYPES, vaccine)) setName(next === "other" ? "" : labelFrom(VACCINE_TYPES, next));
              setVaccine(next);
            }}
            className="input mt-1"
          >
            {VACCINE_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </Field>
        <Field label="Name on the vial" hint="e.g. Tetanus toxoid, TT2.">
          <input required value={name} onChange={(event) => setName(event.target.value)} maxLength={200} className="input mt-1" />
        </Field>
        <Field label="Given on">
          <input required type="date" max={today} value={given} onChange={(event) => setGiven(event.target.value)} className="input mt-1" />
        </Field>
        <Field label="Batch number" optional>
          <input name="batch_number" maxLength={50} className="input mt-1" />
        </Field>
        <Field label="Manufacturer" optional>
          <input name="manufacturer" maxLength={200} className="input mt-1" />
        </Field>
        <Field label="Vial expiry" optional>
          <input type="date" name="expiry_date" className="input mt-1" />
        </Field>
        <Field label="Where on the body" optional hint="e.g. left upper arm.">
          <input name="administration_site" list="vaccine-sites" maxLength={100} className="input mt-1" />
          <datalist id="vaccine-sites">{["Left upper arm", "Right upper arm", "Left thigh", "Right thigh", "Mouth"].map((item) => <option key={item} value={item} />)}</datalist>
        </Field>
        <Field label="Route" optional>
          <input name="administration_route" list="vaccine-routes" maxLength={100} className="input mt-1" />
          <datalist id="vaccine-routes">{["Intramuscular", "Subcutaneous", "Intradermal", "Oral"].map((item) => <option key={item} value={item} />)}</datalist>
        </Field>
      </FormSection>

      <FormSection title="Next dose">
        <Field label="Due on" optional hint="Leave empty if the course is complete.">
          <input type="date" name="next_dose_due" min={given ? new Date(Date.parse(given) + 86_400_000).toISOString().slice(0, 10) : undefined} className="input mt-1" />
        </Field>
        <Field label="Notes on the next dose" optional>
          <input name="next_dose_notes" className="input mt-1" />
        </Field>
      </FormSection>

      <FormSection title="Reaction">
        <Field label="Reaction seen">
          <select value={severity} onChange={(event) => setSeverity(event.target.value)} className="input mt-1">
            {REACTION_SEVERITIES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </Field>
        {severity !== "none" && (
          <Field label="What happened">
            <textarea required name="adverse_reactions" rows={2} className="input mt-1" />
          </Field>
        )}
        <Field label="Other notes" optional className="sm:col-span-2">
          <textarea name="notes" rows={2} className="input mt-1" />
        </Field>
      </FormSection>
    </FormModal>
  );
}

/** Vaccines given at school, and the next doses coming up. */
export function ImmunisationsPanel({ immunisations, learners, records, isLoading, isError, onRetry, canManage, onChanged , initialView}: {
  /** A register view to open on, e.g. "follow-up" from the dashboard. */
  initialView?: string;
  immunisations?: Immunization[];
  learners: ClinicLearner[];
  records: MedicalRecord[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  canManage: boolean;
  onChanged: () => void;
}) {
  const today = todayIso();
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);

  const columns: RegisterColumn<Immunization>[] = [
    {
      header: "Learner",
      cell: (row) => (
        <span>
          <span className="font-medium text-gray-900">{row.student_name}</span>
          <span className="block text-xs text-gray-500">{row.student_id}</span>
        </span>
      ),
      exportValue: (row) => row.student_name,
      className: "min-w-[10rem]",
    },
    { header: "Learner number", cell: (row) => row.student_id, exportValue: (row) => row.student_id, exportOnly: true },
    { header: "Vaccine", cell: (row) => row.vaccine_name, exportValue: (row) => row.vaccine_name, className: "text-gray-700" },
    { header: "Given", cell: (row) => day(row.vaccination_date), exportValue: (row) => day(row.vaccination_date), className: "tabular-nums text-gray-700" },
    { header: "Batch", cell: (row) => row.batch_number || "—", exportValue: (row) => row.batch_number ?? "", className: "text-gray-700" },
    { header: "Given by", cell: (row) => row.administered_by_name, exportValue: (row) => row.administered_by_name, className: "text-gray-700" },
    { header: "Reaction", cell: (row) => <StatusDot label={labelFrom(REACTION_SEVERITIES, row.reaction_severity)} dot={severityDot(row.reaction_severity)} />, exportValue: (row) => labelFrom(REACTION_SEVERITIES, row.reaction_severity) },
    { header: "Next dose", cell: (row) => <StatusDot {...nextDoseStatus(row, today)} />, exportValue: (row) => day(row.next_dose_due) },
  ];

  const row = viewing !== null ? immunisations?.find((entry) => entry.id === viewing) : undefined;

  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        key={initialView}
        initialState={initialView ? { view: initialView } : undefined}
        title="Immunisations"
        description="Vaccines given at school. “Next dose due” lists what falls due within 30 days or is overdue."
        actions={canManage && (
          <button type="button" onClick={() => setAdding(true)} className="btn-primary h-8 min-h-8 px-3 text-xs">
            <Syringe className="mr-1.5 h-3.5 w-3.5" /> Record an immunisation
          </button>
        )}
        rows={immunisations}
        config={immunisationConfig(today)}
        columns={columns}
        rowKey={(entry) => entry.id}
        onOpen={(entry) => setViewing(entry.id)}
        noun={["immunisation", "immunisations"]}
        exportName={{ file: "immunisations", title: "Immunisations Given at School" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText={canManage ? "No immunisations recorded yet." : "No immunisations recorded."}
      />

      {row && (
        <DetailsModal
          title={`${row.vaccine_name} · ${row.student_name}`}
          headline={{ label: "Given on", value: day(row.vaccination_date), note: `by ${row.administered_by_name}` }}
          status={nextDoseStatus(row, today)}
          facts={[
            ["Learner", `${row.student_name} (${row.student_id})`],
            ["Vaccine", labelFrom(VACCINE_TYPES, row.vaccine_type)],
            ["Batch", row.batch_number ?? undefined],
            ["Manufacturer", row.manufacturer ?? undefined],
            ["Vial expiry", day(row.expiry_date) || undefined],
            ["Where on the body", row.administration_site ?? undefined],
            ["Route", row.administration_route ?? undefined],
            ["Next dose", day(row.next_dose_due) || "None"],
            ["Reaction", labelFrom(REACTION_SEVERITIES, row.reaction_severity)],
          ]}
          sections={[
            { title: "What happened", body: row.adverse_reactions, tone: row.reaction_severity === "severe" ? "danger" : "warning" },
            { title: "Notes on the next dose", body: row.next_dose_notes },
            { title: "Notes", body: row.notes },
          ]}
          onClose={() => setViewing(null)}
        />
      )}

      {adding && (
        <ImmunisationForm
          learners={sortLearners(learners)}
          records={records}
          onClose={() => setAdding(false)}
          onDone={(saved) => {
            setAdding(false);
            setNotice({ tone: "success", text: `${saved.vaccine_name} recorded for ${saved.student_name}.${saved.next_dose_due ? ` Next dose due ${day(saved.next_dose_due)}.` : ""}` });
            onChanged();
          }}
        />
      )}
    </div>
  );
}
